import { Component, OnInit, OnDestroy, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { animate, transition, state, style, trigger } from '@angular/animations';
import { AddBookForm } from './add-book-form';
import { InputControlService } from '../../core/services/input-control.service';
import { ModalService } from '../../core/services/modal.service';
import { Book } from '../../classes/book';
import { BooksService } from '../../core/services/data/books.service';
import { UtilitiesService } from '../../core/services/utilities.service';
import { UserService } from '../../core/services/data/user.service';
import { AnalyticsService } from '../../core/services/analytics.service';

import { IUser } from '../../types/datatypes';
import { AuthorAPIResponse, BookAPIResponse, UserAPIResponse } from '../../types/responses';

@Component({
	selector: 'app-add-book',
	templateUrl: './add-book.component.html',
	styleUrls: [
		'../../shared/confirm-modal/confirm-modal.component.scss',
		'../../shared/error-modal/error-modal.component.scss',
		'./add-book.component.scss',
	],
	animations: [
		trigger('fadeOut1', [
			state('true', style({ opacity: '1' })),
			state('false', style({ opacity: '0' })),
			transition('true => false', animate('1s ease-in')),
		]),
		trigger('fadeIn1', [
			state('true', style({ opacity: '1' })),
			state('false', style({ opacity: '0' })),
			transition('false => true', animate('1ms')),
		]),
		trigger('fadeOut2', [
			state('true', style({ opacity: '1' })),
			state('false', style({ opacity: '0' })),
			transition('true => false', animate('1ms')),
		]),
		trigger('fadeIn2', [
			state('true', style({ opacity: '1' })),
			state('false', style({ opacity: '0' })),
			transition('false => true', animate('1s ease-in')),
		]),
	],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class AddBookComponent implements OnInit, OnDestroy {
	@Output() bookAdded = new EventEmitter();

	public confirmText;

	public confirmEvent = null;

	public errorText;

	public successText;

	public buttonText = { confirm: 'Discard Changes', cancel: 'Go Back' };

	public amazonError = false;

	public showContent = true;

	public inProgress = false;

	public amazonInProgress = false;

	public bookImageUploadUrl;

	public bookImageUploadPath;

	public form: UntypedFormGroup;

	public formComponents = new AddBookForm();

	public showForm1 = false;

	public showForm2 = true;

	public withOutAmazon = false;

	public withAmazon = false;

	public book = new Book();

	public otherBooks = [];

	public booksToAdd = [];

	public currentIndex = 0;

	public backgroundImage;

	public showImg1 = true;

	public showImg2 = false;

	public showButtons = true;

	public errorOptions = {
		title: 'Oops!',
		text: null,
		actions: [{
			type: 'solid',
			text: 'Got it',
			callback: () => this.ms.close('ab-error-modal'),
		}],
	};

	private author: IUser;

	private author$;

	constructor(
		private bs: BooksService,
		private ms: ModalService,
		private us: UserService,
		private uss: UtilitiesService,
		private as: AnalyticsService,
		private ics: InputControlService,
	) {
	}

	ngOnInit() {
		this.form = InputControlService.toFormGroup(this.formComponents);
		this.author$ = this.us.author$.subscribe(
			(data: IUser) => {
				this.author = data;
			},
		);
	}

	ngOnDestroy() {
		this.author$.unsubscribe();
	}

	checkScraping() {
		if (!this.showForm2) {
			this.scrapeLink();
		}
	}

	handleKeyDown(event) {
		if (event.key === 'enter' || event.keyCode === 13) {
			event.preventDefault();
			this.checkScraping();
		}
	}

	noAmazon() {
		this.showForm1 = false;
		this.showForm2 = true;
		this.withOutAmazon = true;
		this.form.controls.amazonLink.patchValue('TBD');
	}

	Amazon() {
		this.showForm1 = false;
		this.showForm2 = true;
		this.withOutAmazon = false;
		this.form.controls.amazonLink.patchValue('');
		this.form.controls.title.patchValue('');
		this.form.controls.author.patchValue('');
	}

	save(event) {
		event.preventDefault();

		// this.errorText = [];
		this.errorOptions.text = [];
		if (this.amazonInProgress) {
			return;
		}

		const error = this.ics.checkFormValid(this.form, this.formComponents);
		if (error.hasErrors) {
			// this.errorText = error.errorText;
			this.errorOptions.text = error.errorText;
			this.ms.open('ab-error-modal');
			return;
		}

		this.book.title = this.form.value.title;
		this.book.author = this.form.value.author;
		this.book.amazonLink = this.form.value.amazonLink.replace(/^\s+|\s+$/g, '');
		this.book.imageStoragePath = this.bookImageUploadPath && this.bookImageUploadPath !== '' ? this.bookImageUploadPath : null;
		this.book.description = this.form.value.description;
		this.amazonInProgress = true;
		this.showContent = false;

		this.bs.save(this.book).subscribe(
			(result: BookAPIResponse) => {
				if (!result.success) {
					// this.errorText = [result.message];
					this.errorOptions.text = [result.message];
					this.showContent = true;
					this.amazonInProgress = false;
				} else {
					document.body.classList.remove('modal-open');
					this.bookAdded.emit(this.book);
					if (this.author) {
						this.author.books.push({ _book: result.book._id, ASIN: result.book.ASIN });
						this.us.saveProfile(this.author._id, this.author).subscribe(
							(savedUser: UserAPIResponse) => {
								if (savedUser.success) {
									this.us.updateAuthor(savedUser.user);
									this.amazonInProgress = false;
									this.otherBooks = [];
								}
							},
						);
					}
					this.as.trackEvent('Book Added', {
						'Book Title': this.book.title,
						'Book Author': this.book.author,
						'Amazon Link': this.book.amazonLink,
						'Other Book': false,
					});
					this.as.trackGTMEvent('book_created', {
						bookTitle: this.book.title,
						bookAuthor: this.book.author,
						amazonLink: this.book.amazonLink,
						otherBook: false,
					});
				}
			},
		);
	}

	bookCoverUpload(data) {
		this.book.amazonImageUrl = data.publicUrl;
		this.bookImageUploadUrl = data.publicUrl;
		this.bookImageUploadPath = data.storagePath;
	}

	bookCoverUploadError(event) {
		// this.errorText = event;
		// eslint-disable-next-line max-len
		if (event.includes('Your book cover image is over 12MB which is the maximum amount our system accepts. Please upload a smaller version to continue.')) {
			this.errorOptions.title = 'Book Cover Image is Too Large';
		}
		this.errorOptions.text = event;
		this.ms.open('ab-error-modal');
	}

	isUploadingBookCover(event) {
		this.inProgress = !!event;
	}

	changeOtherBook(value) {
		this.backgroundImage = this.otherBooks[this.currentIndex + value].amazonImageUrl;
		this.showImg1 = false;
		this.showImg2 = true;
		this.showButtons = false;
		setTimeout(() => {
			this.currentIndex += value;
			this.showImg1 = true;
			this.showImg2 = false;
			this.showButtons = true;
		}, 1000);
	}

	addOtherBook() {
		this.booksToAdd.push(this.otherBooks[this.currentIndex]);
		this.otherBooks[this.currentIndex].added = true;
		if (this.currentIndex + 1 < this.otherBooks.length) {
			this.changeOtherBook(this.currentIndex + 1);
		}
	}

	skipOtherBook() {
		this.otherBooks.splice(this.currentIndex, 1);
		if (this.currentIndex + 1 >= this.otherBooks.length) {
			this.currentIndex += -1;
		} else if (this.currentIndex + 1 < this.otherBooks.length) {
			this.currentIndex += 1;
		}
	}

	saveOtherBooks() {
		this.bs.addBooksBulk(this.author, this.booksToAdd).subscribe((result: AuthorAPIResponse) => {
			if (result.success) {
				this.us.updateAuthor(result.author);
			}
			this.booksToAdd.forEach((item) => {
				this.as.trackEvent('Book Added', {
					'Book Author': item.author,
					'Book Title': item.title,
					'Amazon Link': item.amazonLink,
					'Other Book': true,
				});
				this.as.trackGTMEvent('book_created', {
					bookAuthor: item.author,
					bookTitle: item.title,
					amazonLink: item.amazonLink,
					otherBook: true,
				});
			});
		});
		// TODO: save other books
		this.close();
	}

	cancel() {
		this.confirmEvent = 'cancel';

		this.close();
	}

	getAmazonError(data) {
		if (data.message && data.message.Error && data.message.Error[0] && data.message.Error[0].Code && data.message.Error[0].Code[0]) {
			return data.message.Error[0].Code[0];
		}
		return null;
	}

	scrapeLink() {
		this.showForm1 = false;
		let error = false;
		let url = '';

		if (!this.form.controls.amazonLink.value) {
			error = true;
		} else {
			const rawUrl = this.form.controls.amazonLink.value.split('?');
			// eslint-disable-next-line prefer-destructuring
			url = rawUrl[0];
			if (url.toLowerCase().indexOf('amazon.com/') === -1 && url.toLowerCase().indexOf('/dp/') === -1) {
				error = true;
			}
			this.withAmazon = true;
		}

		const ASIN = Book.parseAmazonId(url);
		if (ASIN === 'null' || ASIN === null) {
			error = true;
		}
		if (error) {
			// this.amazonError = false;
			// this.errorText = ['Whoops.  This doesn\'t appear to be a correct Amazon link. Please try again.'];
			this.errorOptions.text = ['Whoops. This doesn\'t appear to be a correct Amazon link. Please try again.'];
			this.ms.open('ab-error-modal');
			this.form.controls.amazonLink.patchValue('https://www.amazon.com/dp/null');
			this.showForm1 = true;
		} else if (!this.amazonInProgress) {
			this.amazonInProgress = true;

			this.bs.getBookData(ASIN).subscribe(
				(result: any) => {
					this.amazonInProgress = false;
					this.showForm2 = true;
					if (!result.success) {
						const amazonError = this.getAmazonError(result);
						if (amazonError === 'InvalidClientTokenId') {
							this.amazonError = true;
						} else {
							this.showForm1 = true;
							this.errorOptions.title = 'Well, This is awkward…';
							this.errorOptions.text = ['It seems Amazon didn’t share your book information with us. 🤔\n'
							+ '\n'
							+ 'No worries!  Just fill out the form below to schedule your Promo, and we’ll take it from there. 📚'];
							this.ms.open('ab-error-modal');
						}
						return;
					}

					this.book.ASIN = ASIN;
					this.book.amazonLink = result.any;
					this.book.author = result.author;

					/*
					 * Normal kindle links pull the author name as `lastName, firstName` whereas
					 * audiobook links are pulled as `firstName, lastName`.
					 */
					this.form.controls.author.patchValue(this.book.author);
					this.book.title = result.title;
					this.form.controls.title.patchValue(this.book.title);

					if (result.cover) {
						this.book.amazonImageUrl = result.cover;
					}

					if (result.otherFormats) {
						result.otherFormats.forEach(({ format, id }) => {
							this.book.whereAvailable[format] = `https://www.amazon.com/dp/${id}`;
						});
					}

					this.book.amazonBookDescription = Book.parseAmazonBookDescription(result.description);
					this.book.lastScraped = parseInt(this.uss.momentInstance().format('YYYYMMDD'), 10);
					this.withAmazon = true;
				},
			);
		}
	}

	dismissAmazonError() {
		this.amazonError = false;
	}

	handleConfirm(event) {
		if (event) {
			this.close();

			if (this.confirmEvent === 'cancel') {
				this.bookAdded.emit(null);
			}

			this.confirmEvent = null;
		}
	}

	close() {
		document.body.classList.remove('modal-open');
		document.getElementById('add-book-form-body').scrollTo(0, 0);
		this.form.reset();
		this.showForm1 = false;
		this.showForm2 = true;
		this.withOutAmazon = false;
		this.withAmazon = false;
		this.showContent = true;
		this.book = new Book();
		this.inProgress = false;
		this.amazonInProgress = false;
		this.bookImageUploadUrl = null;
		this.bookImageUploadPath = null;
		this.otherBooks = [];
		this.booksToAdd = [];
		this.ms.close('add-book-form');
	}
}
